/**
 * ImageAdStudio — the Image Ad Studio workflow (issue #31 + its follow-on).
 *
 * Generation runs through Cipher's real image-generation backend (issue
 * #29) — no engine/model name is exposed here. Angle selection, a full
 * persistent session, a research-capable Image Chat, and a real
 * generate/review/submit sequence were added on top of the original
 * brief-editor MVP without touching the proven generation pipeline.
 *
 * Flow:
 *   1. Pick an offer (URL param `offerId` or dropdown fallback).
 *   2. Start or resume an Image Ad Studio session for that offer.
 *   3. Confirm the offer's psychology map if it isn't already (shared with
 *      the video session's own step — one implementation, reused).
 *   4. Generate angles or write one, review/edit it, select it — it stays
 *      pinned through the rest of the workflow.
 *   5. Talk to Image Chat (persistent, with real read-only web research).
 *   6. Generate a brief; edit its on-image text and platform ad copy,
 *      separately labeled; see a layout preview before generating.
 *   7. Generate images (its own action) → review the finished result next
 *      to its ad copy → select specific variants → Submit (its own,
 *      separate action — approval only, never ad activation or spend).
 *
 * EMBEDDABLE IN v2 (issue #31), SAME COMPONENT, NOT A SECOND ONE
 * ================================================================
 * `offerId` and `onBack` are optional props, exactly as before this work.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import OfferSearchSelect from "./OfferSearchSelect";
import ReferenceImagePanel from "./ReferenceImagePanel";
import { usePsychologyMap } from "../services/usePsychologyMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FORMATS_BY_CHANNEL = {
  META: ["META_SQUARE", "META_LANDSCAPE", "META_STORY"],
  TIKTOK: ["TIKTOK_STORY"],
};

const PLACEMENT_OPTIONS = [
  "top-left", "top-center", "top-right",
  "center",
  "bottom-left", "bottom-center", "bottom-right",
];

const DEFAULT_CTA_BUTTONS = ["Buy Now", "Learn More", "Shop Now", "Get Offer", "Claim Deal"];

// Brand logo (issue #147) — an OPTIONAL logo uploaded ONCE in the brief and
// composited onto EVERY generated image at a corner the operator picks. The
// four corners map to a fractional centre (x, y) on the base; scale is the logo
// width as a fraction of the base width.
const LOGO_CORNERS = [
  { key: "top-left", label: "Top left", x: 0.15, y: 0.12 },
  { key: "top-right", label: "Top right", x: 0.85, y: 0.12 },
  { key: "bottom-left", label: "Bottom left", x: 0.15, y: 0.88 },
  { key: "bottom-right", label: "Bottom right", x: 0.85, y: 0.88 },
];
const DEFAULT_LOGO_CORNER = "bottom-right";
const DEFAULT_LOGO_SCALE = 0.20;
function cornerFromXY(pos) {
  if (!Array.isArray(pos)) return DEFAULT_LOGO_CORNER;
  let best = LOGO_CORNERS[0], bestD = Infinity;
  for (const c of LOGO_CORNERS) {
    const d = Math.abs(c.x - pos[0]) + Math.abs(c.y - pos[1]);
    if (d < bestD) { bestD = d; best = c; }
  }
  return best.key;
}

// negative_prompt intentionally excluded: the real generation backend
// (issue #29) has no negative-prompt capability at all, so showing an
// editable field for it would let an operator fill in something that
// silently has zero effect. Not removed from the brief's data shape
// (still saved/patched if already present on an older brief) — just not
// surfaced as something to edit.

function fmtErr(e, fallback = "request failed") {
  return e?.response?.data?.detail || e?.message || fallback;
}

export default function ImageAdStudio({ offerId: offerIdProp, onBack } = {}) {
  const params = useParams();
  const navigate = useNavigate();
  const embedded = offerIdProp !== undefined;
  const offerIdFromUrl = embedded ? offerIdProp : (params.offerId || "");

  const [offerId, setOfferId] = useState(offerIdFromUrl);
  const [offer, setOffer] = useState(null);
  const [channel, setChannel] = useState("META");
  const [selectedFormats, setSelectedFormats] = useState(["META_SQUARE"]);
  const [landingVariants, setLandingVariants] = useState([]);
  const [landingVariant, setLandingVariant] = useState("");
  const [ctaButtons, setCtaButtons] = useState(DEFAULT_CTA_BUTTONS);

  // ── session: the persisted record this whole feature exists for ────────
  const [session, setSession] = useState(null);
  const [resumableSessions, setResumableSessions] = useState([]);
  const [sessionChoicePending, setSessionChoicePending] = useState(false);
  const [sessionError, setSessionError] = useState(null);

  // ── angle ────────────────────────────────────────────────────────────
  const [angles, setAngles] = useState([]);
  const [angleId, setAngleId] = useState("");
  const [angleName, setAngleName] = useState("");
  const [showWriteOwn, setShowWriteOwn] = useState(false);
  const [angleText, setAngleText] = useState("");
  const [angleSubmitting, setAngleSubmitting] = useState(false);
  const [editingAngle, setEditingAngle] = useState(false);
  const [angleUsage, setAngleUsage] = useState(null);
  const [editAngleName, setEditAngleName] = useState("");
  const [editCoreBelief, setEditCoreBelief] = useState("");
  const [angleEditSaving, setAngleEditSaving] = useState(false);

  // ── Image Chat ───────────────────────────────────────────────────────
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const chatEndRef = useRef(null);

  // ── brief ────────────────────────────────────────────────────────────
  const [brief, setBrief] = useState(null);
  const [briefSaving, setBriefSaving] = useState(false);
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [compliance, setCompliance] = useState(null);
  const [complianceRunning, setComplianceRunning] = useState(false);
  // Brand logo (issue #147): the summary returned by the backend
  // ({has_logo, filename, position:[x,y], scale, ...}) or null.
  const [brandLogo, setBrandLogo] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);

  // ── generation / review / submission ────────────────────────────────
  const [job, setJob] = useState(null);
  const [kickingOff, setKickingOff] = useState(false);
  const [selectedCreatives, setSelectedCreatives] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  // Psychology map — the same implementation the video session uses.
  const psych = usePsychologyMap({
    offer: offer || { offer_id: offerId, psychology_map: [] },
    isNewSession: false,
    onComplete: async () => {
      setOffer((prev) => (prev ? { ...prev, psychology_map_confirmed: true } : prev));
      if (session) {
        try {
          await axios.patch(`${API}/creative/image/sessions/${session.session_id}`,
            { psychology_map_confirmed: true });
        } catch { /* non-fatal — the offer record is the source of truth */ }
      }
    },
  });

  // ── load offer, formats, landing variants, angles, resumable sessions ──
  useEffect(() => {
    if (!offerId) {
      setOffer(null);
      setLandingVariants([]);
      setAngles([]);
      setResumableSessions([]);
      setSession(null);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/offers/${offerId}`, { withCredentials: true });
        setOffer(r.data);
      } catch { /* ignore */ }
      try {
        const r = await axios.get(`${API}/offers/${offerId}/landing-page-variants`);
        setLandingVariants(r.data.variants || []);
      } catch {
        setLandingVariants([]);
      }
      try {
        const r = await axios.get(`${API}/offers/${offerId}/angles`, { withCredentials: true });
        setAngles(r.data.angles || []);
      } catch {
        setAngles([]);
      }
      try {
        const r = await axios.get(`${API}/creative/image/formats`);
        setCtaButtons(r.data.platform_cta_buttons || DEFAULT_CTA_BUTTONS);
      } catch { /* keep default */ }
      try {
        const r = await axios.get(`${API}/creative/image/sessions`, {
          params: { offer_id: offerId, status: "in_progress" }, withCredentials: true,
        });
        const sessions = r.data.sessions || [];
        setResumableSessions(sessions);
        setSessionChoicePending(sessions.length > 0);
      } catch {
        setResumableSessions([]);
      }
    })();
  }, [offerId]);

  const refreshAngles = useCallback(async () => {
    if (!offerId) return;
    try {
      const r = await axios.get(`${API}/offers/${offerId}/angles`, { withCredentials: true });
      setAngles(r.data.angles || []);
    } catch { /* keep prior list */ }
  }, [offerId]);

  const startNewSession = async () => {
    setSessionError(null);
    try {
      const r = await axios.post(`${API}/creative/image/sessions`,
        { offer_id: offerId, channel }, { headers: { "Content-Type": "application/json" } });
      setSession(r.data);
      setSessionChoicePending(false);
    } catch (e) {
      setSessionError(fmtErr(e, "Could not start a session"));
    }
  };

  const resumeSession = async (sessionId) => {
    setSessionError(null);
    try {
      const r = await axios.get(`${API}/creative/image/sessions/${sessionId}`, { withCredentials: true });
      const s = r.data;
      setSession(s);
      setSessionChoicePending(false);
      setChannel(s.channel || "META");
      if (s.angle_id) { setAngleId(s.angle_id); setAngleName(s.angle_name || ""); }
      if (s.draft_brief) setBrief(s.draft_brief);
      setChatMessages(s.chat_transcript || []);
      setSelectedCreatives(new Set(s.selected_creative_ids || []));
      const latestJob = (s.jobs || [])[0];
      if (latestJob) setJob(latestJob);
    } catch (e) {
      setSessionError(fmtErr(e, "Could not resume that session"));
    }
  };

  // ── Reopenable image sessions, cross-offer (issue #111) ─────────────
  // The per-offer resume list can only answer "sessions for THIS offer";
  // once the operator has navigated away from an offer, its sessions were
  // unreachable. This feed lists recent image sessions across every offer so
  // any prior session can be found and reopened by name.
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState(null);
  const [recentScope, setRecentScope] = useState("mine");

  const loadRecent = useCallback(async (scope = recentScope) => {
    setRecentLoading(true);
    setRecentError(null);
    try {
      const r = await axios.get(`${API}/creative/image/recent-sessions`, {
        params: { scope }, withCredentials: true,
      });
      setRecent(r.data?.sessions || []);
    } catch (e) {
      setRecentError(fmtErr(e, "Could not load recent sessions"));
    } finally {
      setRecentLoading(false);
    }
  }, [recentScope]);

  const reopenRecent = async (row) => {
    // Flat image sessions reopen here in the Studio. Multi-body workspaces
    // live in the Creative Workspace surface, so route the operator there
    // rather than pretending to load one into the flat studio.
    if (row.kind === "workspace") {
      navigate(`/creative/workspace/${row.id}`);
      return;
    }
    setOfferId(row.offer_id || "");
    setOffer(row.offer_name ? { offer_id: row.offer_id, name: row.offer_name } : null);
    await resumeSession(row.id);
  };

  const handleOfferChange = (newOfferId, offerDoc) => {
    setOfferId(newOfferId);
    setOffer(offerDoc || null);
    setBrief(null);
    setCompliance(null);
    setJob(null);
    setLandingVariant("");
    setAngleId("");
    setAngleName("");
    setSession(null);
    setChatMessages([]);
    setSelectedCreatives(new Set());
    if (!newOfferId) setLandingVariants([]);
  };

  // ── poll job every 5s while queued/in-progress ──────────────────────
  const pollJob = useCallback(async () => {
    if (!job || !job.job_id) return;
    if (["complete", "failed"].includes(job.status)) return;
    try {
      const r = await axios.get(`${API}/creative/image/jobs/${job.job_id}`);
      setJob(r.data);
    } catch { /* keep previous state */ }
  }, [job]);

  useEffect(() => {
    if (!job || ["complete", "failed"].includes(job.status)) return;
    const id = setInterval(pollJob, 5000);
    return () => clearInterval(id);
  }, [job, pollJob]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // ── angle actions ────────────────────────────────────────────────────
  const handleGenerateAngles = async () => {
    if (!session) return;
    setAngleSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/offers/${offerId}/angles/generate`,
        { session_id: session.session_id },
        { headers: { "Content-Type": "application/json" } });
      await refreshAngles();
    } catch (e) {
      setError(fmtErr(e, "Angle generation failed"));
    } finally {
      setAngleSubmitting(false);
    }
  };

  const applyAngleSelection = async (id, name) => {
    setAngleId(id);
    setAngleName(name);
    setBrief(null); // a brief already built around the old angle no longer applies
    if (session) {
      try {
        await axios.patch(`${API}/creative/image/sessions/${session.session_id}`,
          { angle_id: id, angle_name: name });
      } catch { /* draft autosave is best-effort */ }
    }
  };

  const handleUseGeneratedAngle = async (a) => {
    try {
      await axios.post(`${API}/offers/${offerId}/angles/${a.angle_id}/select`, {},
        { withCredentials: true });
    } catch { /* selection is advisory here — the session's angle_id is authoritative */ }
    await applyAngleSelection(a.angle_id, a.angle_name || a.angle_id);
  };

  const handleWriteOwnSubmit = async () => {
    const text = angleText.trim();
    if (!text) return;
    setAngleSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/offers/${offerId}/angles/custom-simple`,
        { angle_text: text }, { headers: { "Content-Type": "application/json" } });
      const a = r.data.angle;
      await refreshAngles();
      await applyAngleSelection(a.angle_id, a.angle_name);
      setShowWriteOwn(false);
      setAngleText("");
    } catch (e) {
      setError(fmtErr(e, "Could not create that angle"));
    } finally {
      setAngleSubmitting(false);
    }
  };

  const startEditAngle = async () => {
    setError(null);
    try {
      const r = await axios.get(`${API}/offers/${offerId}/angles/${angleId}/usage`,
        { withCredentials: true });
      setAngleUsage(r.data);
    } catch {
      // Read failed — treat as "unknown, assume shared" so the safer (fork)
      // path is offered rather than silently allowing an in-place edit we
      // couldn't actually confirm was safe.
      setAngleUsage({ used_elsewhere: true, used_by: [{ kind: "unknown (usage check failed)", count: 0 }] });
    }
    const current = angles.find((a) => a.angle_id === angleId);
    setEditAngleName(current?.angle_name || angleName);
    setEditCoreBelief(current?.core_belief_shift || "");
    setEditingAngle(true);
  };

  const saveAngleEdit = async () => {
    if (!editAngleName.trim() || !editCoreBelief.trim()) {
      setError("Angle name and core belief shift cannot be empty.");
      return;
    }
    setAngleEditSaving(true);
    setError(null);
    try {
      if (angleUsage?.used_elsewhere) {
        // Fork — do not mutate a record hooks/scripts/creatives already
        // reference. Same simple path as "write my own", seeded with the
        // edited text.
        const r = await axios.post(`${API}/offers/${offerId}/angles/custom-simple`,
          { angle_text: `${editAngleName}. ${editCoreBelief}` },
          { headers: { "Content-Type": "application/json" } });
        await refreshAngles();
        await applyAngleSelection(r.data.angle.angle_id, r.data.angle.angle_name);
      } else {
        await axios.patch(`${API}/offers/${offerId}/angles/${angleId}`,
          { angle_name: editAngleName, core_belief_shift: editCoreBelief },
          { headers: { "Content-Type": "application/json" } });
        await refreshAngles();
        await applyAngleSelection(angleId, editAngleName);
      }
      setEditingAngle(false);
    } catch (e) {
      setError(fmtErr(e, "Could not save that edit"));
    } finally {
      setAngleEditSaving(false);
    }
  };

  // ── Image Chat ───────────────────────────────────────────────────────
  const sendChatMessage = async () => {
    const text = chatInput.trim();
    if (!text || !session) return;
    setChatSending(true);
    setChatInput("");
    setChatMessages((prev) => [...prev, { role: "user", content: text, sources: [] }]);
    try {
      const r = await axios.post(`${API}/creative/image/sessions/${session.session_id}/chat`,
        { message: text }, { headers: { "Content-Type": "application/json" } });
      setChatMessages((prev) => [...prev, {
        role: "assistant", content: r.data.assistant, sources: r.data.sources || [],
      }]);
    } catch (e) {
      setChatMessages((prev) => [...prev, {
        role: "assistant", content: `Error: ${fmtErr(e)}`, sources: [],
      }]);
    } finally {
      setChatSending(false);
    }
  };

  // ── brief actions ────────────────────────────────────────────────────
  const handleGenerateBrief = async () => {
    setError(null);
    setGeneratingBrief(true);
    try {
      const r = await axios.post(
        `${API}/creative/image/brief`,
        {
          offer_id: offerId, channel,
          ...(angleId ? { angle_id: angleId } : {}),
          ...(session ? { session_id: session.session_id } : {}),
        },
        { headers: { "Content-Type": "application/json" } },
      );
      setBrief(r.data);
      setCompliance(null);
      // A freshly generated brief carries no logo yet (a regenerate mints a new
      // brief_id); reflect any that the backend returned on the new brief.
      setBrandLogo(r.data?.brand_logo?.has_logo ? r.data.brand_logo : null);
      setJob(null);
      setSelectedCreatives(new Set());
    } catch (e) {
      setError(fmtErr(e, "Brief generation failed"));
    } finally {
      setGeneratingBrief(false);
    }
  };

  const handleSaveBrief = async () => {
    if (!brief) return;
    setBriefSaving(true);
    try {
      const patch = {
        primary_headline: brief.primary_headline,
        subheadline: brief.subheadline,
        body_copy: brief.body_copy,
        cta_text: brief.cta_text,
        platform_cta_button: brief.platform_cta_button,
        visual_concept: brief.visual_concept,
        colour_palette: brief.colour_palette,
        text_overlay_placement: brief.text_overlay_placement,
        negative_prompt: brief.negative_prompt,
        compliance_notes: brief.compliance_notes,
      };
      await axios.patch(`${API}/creative/image/brief/${brief.brief_id}`, patch, {
        headers: { "Content-Type": "application/json" },
      });
      if (session) {
        try {
          await axios.patch(`${API}/creative/image/sessions/${session.session_id}`,
            { draft_brief: brief });
        } catch { /* autosave is best-effort */ }
      }
    } catch (e) {
      setError(fmtErr(e, "Brief save failed"));
    } finally {
      setBriefSaving(false);
    }
  };

  const handleRunCompliance = async () => {
    if (!brief) return;
    setComplianceRunning(true);
    try {
      await handleSaveBrief();
      const r = await axios.post(
        `${API}/creative/image/brief/${brief.brief_id}/compliance-audit`,
        {}, { withCredentials: true },
      );
      setCompliance(r.data.audit);
    } catch (e) {
      setError(fmtErr(e, "Compliance audit failed"));
    } finally {
      setComplianceRunning(false);
    }
  };

  // ── brand logo (issue #147) ──────────────────────────────────────────
  const handleUploadBrandLogo = async (file) => {
    if (!brief || !file) return;
    setLogoBusy(true);
    setError(null);
    try {
      const corner = LOGO_CORNERS.find((c) => c.key === DEFAULT_LOGO_CORNER);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("x", String(corner.x));
      fd.append("y", String(corner.y));
      fd.append("scale", String(DEFAULT_LOGO_SCALE));
      const r = await axios.post(
        `${API}/creative/image/brief/${brief.brief_id}/brand-logo`,
        fd, { withCredentials: true },
      );
      setBrandLogo(r.data.brand_logo);
    } catch (e) {
      setError(fmtErr(e, "Brand logo upload failed"));
    } finally {
      setLogoBusy(false);
    }
  };

  const handleLogoPlacement = async (patch) => {
    if (!brief || !brandLogo) return;
    setLogoBusy(true);
    setError(null);
    try {
      const r = await axios.patch(
        `${API}/creative/image/brief/${brief.brief_id}/brand-logo`,
        patch, { withCredentials: true },
      );
      setBrandLogo(r.data.brand_logo);
    } catch (e) {
      setError(fmtErr(e, "Brand logo update failed"));
    } finally {
      setLogoBusy(false);
    }
  };

  const handleRemoveBrandLogo = async () => {
    if (!brief) return;
    setLogoBusy(true);
    setError(null);
    try {
      await axios.delete(
        `${API}/creative/image/brief/${brief.brief_id}/brand-logo`,
        { withCredentials: true },
      );
      setBrandLogo(null);
    } catch (e) {
      setError(fmtErr(e, "Brand logo removal failed"));
    } finally {
      setLogoBusy(false);
    }
  };

  const handleKickoff = async () => {
    if (!brief || selectedFormats.length === 0) return;
    setKickingOff(true);
    try {
      await handleSaveBrief();
      const r = await axios.post(
        `${API}/creative/image/brief/${brief.brief_id}/generate`,
        { formats: selectedFormats, landing_page_variant: landingVariant || null },
        { headers: { "Content-Type": "application/json" } },
      );
      setJob(r.data);
      setSelectedCreatives(new Set());
    } catch (e) {
      setError(fmtErr(e, "Generation kickoff failed"));
    } finally {
      setKickingOff(false);
    }
  };

  const handleRetryDelivery = async (creativeId) => {
    setRetryingId(creativeId);
    try {
      await axios.post(`${API}/creative/image/creatives/${creativeId}/retry-delivery`, {});
      await pollJob();
    } catch (e) {
      setError(fmtErr(e, "Retry failed"));
    } finally {
      setRetryingId(null);
    }
  };

  // ── review / selection / submit ─────────────────────────────────────
  const toggleCreativeSelected = async (creativeId) => {
    const next = new Set(selectedCreatives);
    if (next.has(creativeId)) next.delete(creativeId); else next.add(creativeId);
    setSelectedCreatives(next);
    if (session) {
      try {
        await axios.post(`${API}/creative/image/sessions/${session.session_id}/selection`,
          { creative_ids: Array.from(next) },
          { headers: { "Content-Type": "application/json" } });
      } catch { /* best-effort — Submit sends the same list explicitly anyway */ }
    }
  };

  const handleSubmit = async () => {
    if (!job || !job.job_id || selectedCreatives.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/creative/image/jobs/${job.job_id}/approve`,
        { creative_ids: Array.from(selectedCreatives) },
        { headers: { "Content-Type": "application/json" } });
      if (session) {
        try {
          await axios.post(`${API}/creative/image/sessions/${session.session_id}/submit`, {});
        } catch { /* non-fatal — the approval itself is the thing that matters */ }
      }
      await pollJob();
    } catch (e) {
      setError(fmtErr(e, "Submit failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFormat = (fmt) => {
    setSelectedFormats((prev) =>
      prev.includes(fmt) ? prev.filter((x) => x !== fmt) : [...prev, fmt]);
  };

  const updateBriefField = (k, v) => {
    setBrief((prev) => ({ ...prev, [k]: v }));
  };

  const updatePlacement = (which, v) => {
    setBrief((prev) => ({
      ...prev,
      text_overlay_placement: { ...(prev.text_overlay_placement || {}), [which]: v },
    }));
  };

  const psychConfirmed = !!offer?.psychology_map_confirmed;
  const readyCreatives = (job?.creatives || []).filter((c) => c.status === "ready");

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6" data-testid="image-ad-studio">
      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Image Ad Studio</h1>
            <p className="text-slate-400 text-sm mt-1">
              Angle → Chat → Brief → Generate → Review → Submit · split-test-ready
            </p>
          </div>
          <button
            onClick={() => (onBack ? onBack() : navigate(-1))}
            className="text-sm text-slate-400 hover:text-white"
            data-testid="image-ad-back-btn"
          >
            ← Back
          </button>
        </header>

        {/* Offer */}
        <section className="bg-slate-900 rounded-xl p-5 border border-slate-800 space-y-3">
          {embedded ? (
            <div className="flex items-center gap-3">
              <label className="text-xs uppercase tracking-wider text-slate-400 w-32">Offer</label>
              <div className="text-sm text-slate-200">
                {offer?.name || offer?.offer_code || offerId}
                {offer?.vertical && <span className="text-slate-400"> · {offer.vertical}</span>}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <label className="text-xs uppercase tracking-wider text-slate-400 w-32 pt-2">
                  Offer <span className="text-rose-400">*</span>
                </label>
                <div className="flex-1">
                  <OfferSearchSelect
                    value={offerId}
                    onChange={handleOfferChange}
                    placeholder="Type to search offers — name, code, or id"
                    required
                    testIdPrefix="image-ad-offer"
                  />
                </div>
              </div>
              {offer && (
                <div className="text-xs text-slate-400 pl-[8.5rem]">
                  <span className="text-emerald-400">✓</span> {offer.name || offer.offer_code || "—"}
                  {offer.vertical && <span> · {offer.vertical}</span>}
                </div>
              )}
            </>
          )}
        </section>

        {/* Reopen a previous image session (across every offer) */}
        {!session && (
          <section className="bg-slate-900 rounded-xl p-5 border border-slate-800 space-y-3"
                   data-testid="image-ad-recent-sessions">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Reopen a previous session</h2>
              <div className="flex items-center gap-2">
                <select
                  value={recentScope}
                  onChange={(e) => { setRecentScope(e.target.value); loadRecent(e.target.value); }}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs"
                  data-testid="image-ad-recent-scope"
                >
                  <option value="mine">Mine</option>
                  <option value="all">Everyone</option>
                </select>
                <button
                  onClick={() => loadRecent()}
                  className="px-3 py-1.5 rounded bg-slate-800 border border-slate-700 hover:border-indigo-500 text-xs"
                  data-testid="image-ad-recent-refresh"
                >
                  {recentLoading ? "Loading…" : recent.length ? "Refresh" : "Show recent sessions"}
                </button>
              </div>
            </div>
            {recentError && <div className="text-sm text-red-300">{recentError}</div>}
            {!recentLoading && recent.length === 0 && (
              <p className="text-sm text-slate-500 italic" data-testid="image-ad-recent-empty">
                {recentError ? "" : "No image sessions loaded yet — click “Show recent sessions”."}
              </p>
            )}
            {recent.length > 0 && (
              <div className="space-y-1.5">
                {recent.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => reopenRecent(s)}
                    className="w-full text-left px-3 py-2 rounded bg-slate-800 border border-slate-700 hover:border-indigo-500 text-sm flex items-center justify-between gap-3"
                    data-testid="image-ad-recent-row"
                  >
                    <span className="truncate">
                      <span className="text-slate-200">{s.display_name || s.id}</span>
                      <span className="text-[11px] text-slate-500">
                        {" · "}{s.kind === "workspace" ? `${s.body_count ?? 0} bodies` : `${s.job_count ?? 0} jobs`}
                        {s.status ? ` · ${s.status}` : ""}
                      </span>
                    </span>
                    <span className="text-[11px] text-slate-500 shrink-0">
                      {s.kind === "workspace" ? "Workspace →" : "Open →"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {error && (
          <div className="bg-red-950/40 border border-red-800 text-red-200 rounded-lg p-4 text-sm"
               data-testid="image-ad-error">
            {error}
          </div>
        )}

        {/* Session: start or resume */}
        {offerId && !session && (
          <section className="bg-slate-900 rounded-xl p-5 border border-slate-800 space-y-3"
                    data-testid="image-ad-session-picker">
            <h2 className="text-lg font-semibold">Session</h2>
            {sessionError && <div className="text-sm text-red-300">{sessionError}</div>}
            {sessionChoicePending && resumableSessions.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-slate-400">
                  You have {resumableSessions.length} session{resumableSessions.length === 1 ? "" : "s"}
                  {" "}already in progress for this offer:
                </p>
                {resumableSessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => resumeSession(s.session_id)}
                    className="w-full text-left px-3 py-2 rounded bg-slate-800 border border-slate-700 hover:border-indigo-500 text-sm"
                    data-testid="image-ad-resume-session-btn"
                  >
                    {s.display_name || s.angle_name || "No angle chosen yet"} · updated {new Date(s.updated_at).toLocaleString()}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3">
              <label className="text-xs uppercase tracking-wider text-slate-400 w-32">Channel</label>
              <select
                value={channel}
                onChange={(e) => {
                  setChannel(e.target.value);
                  setSelectedFormats([FORMATS_BY_CHANNEL[e.target.value][0]]);
                }}
                className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                data-testid="image-ad-channel-select"
              >
                <option value="META">Meta</option>
                <option value="TIKTOK">TikTok</option>
              </select>
            </div>
            <button
              onClick={startNewSession}
              className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 text-sm font-medium"
              data-testid="image-ad-start-session-btn"
            >
              Start new session
            </button>
          </section>
        )}

        {session && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="image-ad-session-grid">
            {/* ── LEFT: the workflow (~2/3) ── */}
            <div className="lg:col-span-2 space-y-6" data-testid="image-ad-workflow-col">
            {/* Psychology map — same step the video session uses, only shown
                when this offer doesn't already have a confirmed one. */}
            {!psychConfirmed && (
              <section className="bg-slate-900 rounded-xl p-5 border border-amber-800/60 space-y-3"
                        data-testid="image-ad-psych-map">
                <h2 className="text-lg font-semibold">Confirm the psychology map first</h2>
                <p className="text-sm text-slate-400">
                  Angle generation needs a confirmed psychology map for this offer — the psychological
                  entry points and beliefs its creative is grounded in. This is shared with the video
                  session; once confirmed here, it's confirmed everywhere. <strong>Writing your own
                  angle doesn't need this</strong> and stays available below.
                </p>
                {psych.mapError && <div className="text-sm text-red-300">{psych.mapError}</div>}
                {psych.mapNote && <div className="text-sm text-amber-300">{psych.mapNote}</div>}
                <div className="text-sm text-slate-300">
                  {psych.entries.length} entr{psych.entries.length === 1 ? "y" : "ies"} on screen.
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => psych.generateMap(psych.entries.length > 0)}
                    disabled={psych.generating}
                    className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm disabled:opacity-50"
                    data-testid="image-ad-psych-generate-btn"
                  >
                    {psych.generating ? "Working…" : psych.entries.length > 0 ? "Regenerate" : "Generate"}
                  </button>
                  <button
                    onClick={psych.confirmAndProceed}
                    disabled={psych.loading || psych.entries.length === 0}
                    className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700"
                    data-testid="image-ad-psych-confirm-btn"
                  >
                    {psych.loading ? "Confirming…" : "Confirm map"}
                  </button>
                </div>
              </section>
            )}

            {/* Angle */}
            <section className="bg-slate-900 rounded-xl p-5 border border-slate-800 space-y-4"
                      data-testid="image-ad-angle-section">
              <h2 className="text-lg font-semibold">Angle</h2>

              {angleId ? (
                editingAngle ? (
                  <div className="space-y-3">
                    {angleUsage?.used_elsewhere && (
                      <div className="text-sm text-amber-300 bg-amber-950/30 border border-amber-800/60 rounded p-3">
                        This angle is already used by {angleUsage.used_by.map((u) => `${u.count} ${u.kind}${u.count === 1 ? "" : "s"}`).join(", ")}.
                        Saving will create a <strong>new</strong> angle from your edit instead of changing the shared one.
                      </div>
                    )}
                    <input
                      value={editAngleName}
                      onChange={(e) => setEditAngleName(e.target.value)}
                      placeholder="Angle name"
                      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                      data-testid="image-ad-angle-edit-name"
                    />
                    <textarea
                      value={editCoreBelief}
                      onChange={(e) => setEditCoreBelief(e.target.value)}
                      placeholder="Core belief shift"
                      rows={2}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                      data-testid="image-ad-angle-edit-belief"
                    />
                    <div className="flex gap-3">
                      <button onClick={saveAngleEdit} disabled={angleEditSaving}
                              className="px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-sm disabled:opacity-50"
                              data-testid="image-ad-angle-edit-save-btn">
                        {angleEditSaving ? "Saving…" : "Save"}
                      </button>
                      <button onClick={() => setEditingAngle(false)}
                              className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-slate-800 border border-slate-700 rounded px-4 py-3"
                       data-testid="image-ad-angle-pinned">
                    <div className="text-sm">
                      <span className="text-slate-400">Working angle: </span>
                      <span className="font-medium">{angleName}</span>
                    </div>
                    <button onClick={startEditAngle} className="text-xs text-indigo-300 hover:text-indigo-200"
                            data-testid="image-ad-angle-edit-btn">
                      Edit
                    </button>
                  </div>
                )
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleGenerateAngles}
                      disabled={!psychConfirmed || angleSubmitting}
                      title={!psychConfirmed ? "Confirm the psychology map above first" : undefined}
                      className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 text-sm font-medium"
                      data-testid="image-ad-generate-angles-btn"
                    >
                      {angleSubmitting ? "Generating…" : "Generate angles"}
                    </button>
                    <button
                      onClick={() => setShowWriteOwn((v) => !v)}
                      className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm font-medium"
                      data-testid="image-ad-write-own-toggle-btn"
                    >
                      Write my own angle
                    </button>
                  </div>
                  {!psychConfirmed && (
                    <p className="text-xs text-amber-400">
                      Generate needs a confirmed psychology map for this offer — confirm it above, or write your own angle now.
                    </p>
                  )}

                  {showWriteOwn && (
                    <div className="space-y-2" data-testid="image-ad-write-own-form">
                      <textarea
                        value={angleText}
                        onChange={(e) => setAngleText(e.target.value)}
                        placeholder="Describe the angle in your own words — one or two sentences"
                        rows={2}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                        data-testid="image-ad-write-own-textbox"
                      />
                      <button onClick={handleWriteOwnSubmit} disabled={angleSubmitting || !angleText.trim()}
                              className="px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-sm disabled:opacity-50"
                              data-testid="image-ad-write-own-submit-btn">
                        {angleSubmitting ? "Creating…" : "Use this angle"}
                      </button>
                    </div>
                  )}

                  {angles.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs uppercase tracking-wider text-slate-400">Pick one</div>
                      {angles.map((a) => (
                        <div key={a.angle_id}
                             className="bg-slate-800 border border-slate-700 rounded px-3 py-2 flex items-center justify-between"
                             data-testid="image-ad-angle-row">
                          <div className="text-sm">
                            {a.angle_name}
                            {a.fields_inferred && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-400"
                                    title="These fields were AI-inferred from your text, not confirmed research">
                                AI inferred
                              </span>
                            )}
                          </div>
                          <button onClick={() => handleUseGeneratedAngle(a)}
                                  className="text-xs text-indigo-300 hover:text-indigo-200"
                                  data-testid="image-ad-angle-use-btn">
                            Use this angle
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Formats + LP variant */}
            <section className="bg-slate-900 rounded-xl p-5 border border-slate-800 space-y-4">
              <div className="flex items-center gap-3">
                <label className="text-xs uppercase tracking-wider text-slate-400 w-32">Formats</label>
                <div className="flex flex-wrap gap-2">
                  {FORMATS_BY_CHANNEL[channel].map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => toggleFormat(fmt)}
                      className={`px-3 py-1.5 rounded text-xs border ${
                        selectedFormats.includes(fmt)
                          ? "bg-emerald-500/20 border-emerald-500 text-emerald-300"
                          : "bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-500"
                      }`}
                      data-testid={`image-ad-format-${fmt}`}
                    >
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>
              {landingVariants.length > 0 && (
                <div className="flex items-center gap-3">
                  <label className="text-xs uppercase tracking-wider text-slate-400 w-32">LP variant</label>
                  <select
                    value={landingVariant}
                    onChange={(e) => setLandingVariant(e.target.value)}
                    className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                    data-testid="image-ad-lp-variant-select"
                  >
                    <option value="">— split test off —</option>
                    {landingVariants.map((v) => (
                      <option key={v.variant_id} value={v.variant_id}>{v.label || v.variant_id}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={handleGenerateBrief}
                disabled={!offerId || !angleId || generatingBrief}
                title={!angleId ? "Pick or write an angle first" : undefined}
                className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 text-sm font-medium"
                data-testid="image-ad-generate-brief-btn"
              >
                {generatingBrief ? "Drafting brief…" : brief ? "Regenerate brief" : "Generate brief"}
              </button>
            </section>

            {/* Brief editor — fields split by destination */}
            {brief && (
              <section className="bg-slate-900 rounded-xl p-5 border border-slate-800 space-y-5"
                        data-testid="image-ad-brief-editor">
                <h2 className="text-lg font-semibold">Brief</h2>

                <div className="border border-slate-700 rounded-lg p-4 space-y-3">
                  <div className="text-xs uppercase tracking-wider text-emerald-400">On the image</div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                      On-Image Headline (≤40 chars)
                    </label>
                    <div className="flex gap-2">
                      <textarea value={brief.primary_headline || ""} rows={2}
                        onChange={(e) => updateBriefField("primary_headline", e.target.value)}
                        className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm font-mono"
                        data-testid="image-ad-brief-primary_headline" />
                      <select value={brief.text_overlay_placement?.headline_position || "top-center"}
                              onChange={(e) => updatePlacement("headline_position", e.target.value)}
                              className="bg-slate-800 border border-slate-700 rounded px-2 text-xs"
                              data-testid="image-ad-brief-headline-placement">
                        {PLACEMENT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                      On-Image CTA Text
                    </label>
                    <div className="flex gap-2">
                      <textarea value={brief.cta_text || ""} rows={2}
                        onChange={(e) => updateBriefField("cta_text", e.target.value)}
                        className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm font-mono"
                        data-testid="image-ad-brief-cta_text" />
                      <select value={brief.text_overlay_placement?.cta_position || "bottom-center"}
                              onChange={(e) => updatePlacement("cta_position", e.target.value)}
                              className="bg-slate-800 border border-slate-700 rounded px-2 text-xs"
                              data-testid="image-ad-brief-cta-placement">
                        {PLACEMENT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-700 rounded-lg p-4 space-y-3">
                  <div className="text-xs uppercase tracking-wider text-indigo-400">
                    For the ad platform — not shown on the image
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                      Ad Headline (platform's own headline field)
                    </label>
                    <textarea value={brief.subheadline || ""} rows={2}
                      onChange={(e) => updateBriefField("subheadline", e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm font-mono"
                      data-testid="image-ad-brief-subheadline" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                      Primary Text (platform's ad-copy field)
                    </label>
                    <textarea value={brief.body_copy || ""} rows={3}
                      onChange={(e) => updateBriefField("body_copy", e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm font-mono"
                      data-testid="image-ad-brief-body_copy" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                      Platform CTA Button
                    </label>
                    <select value={brief.platform_cta_button || "Learn More"}
                            onChange={(e) => updateBriefField("platform_cta_button", e.target.value)}
                            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                            data-testid="image-ad-brief-platform_cta_button">
                      {ctaButtons.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                      Visual concept
                    </label>
                    <textarea value={brief.visual_concept || ""} rows={3}
                      onChange={(e) => updateBriefField("visual_concept", e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm font-mono"
                      data-testid="image-ad-brief-visual_concept" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                      Colour palette
                    </label>
                    <ColourPaletteEditor
                      value={brief.colour_palette || []}
                      onChange={(next) => updateBriefField("colour_palette", next)}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                      Compliance notes
                    </label>
                    <textarea value={brief.compliance_notes || ""} rows={2}
                      onChange={(e) => updateBriefField("compliance_notes", e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm font-mono"
                      data-testid="image-ad-brief-compliance_notes" />
                  </div>
                </div>

                {/* Layout preview — where the two on-image fields will sit,
                    before a real image exists. Not the final photo. */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">
                    Layout preview (not the final image)
                  </div>
                  <LayoutPreview brief={brief} format={selectedFormats[0]} />
                </div>

                {/* Brand logo (optional) — issue #147. Uploaded ONCE here and
                    composited (auto-cleaned) onto EVERY generated image at the
                    chosen corner + scale. Distinct from the separate Composite
                    panel, which only ever stamped a one-off asset onto a single
                    image and never reached a generation. */}
                <div className="border border-slate-800 rounded-lg p-3 bg-slate-950/40"
                     data-testid="image-ad-brand-logo">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">
                      Brand logo (optional)
                    </div>
                    {brandLogo && (
                      <button onClick={handleRemoveBrandLogo} disabled={logoBusy}
                              className="text-xs text-slate-400 hover:text-red-300 disabled:opacity-50"
                              data-testid="image-ad-brand-logo-remove">
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    Applied to <span className="text-slate-300">every</span> generated
                    image — a plain logo is auto-cleaned (its background dropped) and
                    placed exactly, the same way the headline and CTA are.
                  </p>
                  {!brandLogo ? (
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-indigo-300 hover:text-indigo-200">
                      <input type="file" accept="image/*" className="hidden"
                             disabled={logoBusy}
                             data-testid="image-ad-brand-logo-input"
                             onChange={(e) => {
                               const f = e.target.files && e.target.files[0];
                               if (f) handleUploadBrandLogo(f);
                               e.target.value = "";
                             }} />
                      <span className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">
                        {logoBusy ? "Uploading…" : "Upload brand logo"}
                      </span>
                    </label>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-xs text-emerald-300 font-mono truncate"
                           data-testid="image-ad-brand-logo-name">
                        ✓ {brandLogo.filename || "logo"} — applied to every image
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        <div>
                          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                            Position
                          </label>
                          <select
                            value={cornerFromXY(brandLogo.position)}
                            disabled={logoBusy}
                            data-testid="image-ad-brand-logo-corner"
                            onChange={(e) => {
                              const c = LOGO_CORNERS.find((k) => k.key === e.target.value);
                              if (c) handleLogoPlacement({ x: c.x, y: c.y });
                            }}
                            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm">
                            {LOGO_CORNERS.map((c) => (
                              <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                            Size · {Math.round((brandLogo.scale || DEFAULT_LOGO_SCALE) * 100)}% width
                          </label>
                          <input type="range" min="10" max="40" step="1"
                                 disabled={logoBusy}
                                 data-testid="image-ad-brand-logo-scale"
                                 value={Math.round((brandLogo.scale || DEFAULT_LOGO_SCALE) * 100)}
                                 onChange={(e) => handleLogoPlacement({ scale: Number(e.target.value) / 100 })}
                                 className="w-40" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 pt-2">
                  <button onClick={handleSaveBrief} disabled={briefSaving}
                          className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm disabled:opacity-50"
                          data-testid="image-ad-save-brief-btn">
                    {briefSaving ? "Saving…" : "Save edits"}
                  </button>
                  <button onClick={handleRunCompliance} disabled={complianceRunning}
                          className="px-3 py-2 rounded bg-amber-600/90 hover:bg-amber-500 text-sm disabled:opacity-50"
                          data-testid="image-ad-run-compliance-btn">
                    {complianceRunning ? "Auditing…" : "Run compliance"}
                  </button>
                  <button onClick={handleKickoff} disabled={kickingOff || selectedFormats.length === 0}
                          className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-700"
                          data-testid="image-ad-kickoff-btn">
                    {kickingOff ? "Generating…" : `Generate ${selectedFormats.length} image${selectedFormats.length === 1 ? "" : "s"}`}
                  </button>
                </div>

                {compliance && (
                  <div className="mt-3 bg-amber-950/30 border border-amber-800/60 rounded p-3 text-sm"
                       data-testid="image-ad-compliance-panel">
                    <div className="text-amber-200 font-medium mb-1">Compliance · {compliance.summary}</div>
                    {(compliance.flags || []).map((f, i) => (
                      <div key={i} className="text-xs text-amber-100 pl-3 border-l border-amber-700/60 my-1">
                        <span className="font-mono">{f.severity || "?"}</span>{" · "}<span>{f.reason}</span>
                        {f.rewrite && <div className="text-emerald-200 mt-0.5">Rewrite: {f.rewrite}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Reference image — the same shared capability the Creative
                Workspace exposes (issue #140). Bring your own image into the
                pipeline (Inspire / Composite / Edit) alongside the brief-driven
                generation above; it never activates an ad or authorizes spend. */}
            <div data-testid="image-ad-reference-panel">
              <ReferenceImagePanel offerId={offerId} />
            </div>

            {/* Review — the finished result, ad copy, selection, submit */}
            {job && (
              <section className="bg-slate-900 rounded-xl p-5 border border-slate-800 space-y-4"
                        data-testid="image-ad-job-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Review</h2>
                    <div className="text-xs text-slate-400 font-mono">
                      {job.job_id} · status:{" "}
                      <span className={
                        job.status === "complete" ? "text-emerald-400"
                          : job.status === "failed" ? "text-red-400" : "text-amber-300"
                      }>{job.status}</span>
                    </div>
                  </div>
                  {readyCreatives.length > 0 && (
                    <button onClick={handleSubmit} disabled={submitting || selectedCreatives.size === 0}
                            className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 text-sm font-medium disabled:bg-slate-700 disabled:text-slate-500"
                            data-testid="image-ad-submit-btn"
                            title="Approves your selected variants only. Does not activate an ad or spend.">
                      {submitting ? "Submitting…" : `Submit ${selectedCreatives.size} selected`}
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Submitting approves only the variants you've checked below — it never activates an ad
                  or authorizes spend.
                </p>
                <div className="space-y-3">
                  {(job.creatives || []).map((c, i) => (
                    <CreativeReviewRow
                      key={c.creative_id || i}
                      c={c}
                      selected={selectedCreatives.has(c.creative_id)}
                      onToggle={() => toggleCreativeSelected(c.creative_id)}
                      onRetry={() => handleRetryDelivery(c.creative_id)}
                      retrying={retryingId === c.creative_id}
                    />
                  ))}
                  {(job.creatives || []).length === 0 && (
                    <div className="text-sm text-slate-400 italic">
                      Generating… {job.formats_requested?.length || 0} formats queued.
                    </div>
                  )}
                </div>
              </section>
            )}
            </div>{/* ── end LEFT workflow column ── */}

            {/* ── RIGHT: persistent Image Chat (~1/3, sticky, full-height) ── */}
            <div className="lg:col-span-1">
              <div
                className="lg:sticky lg:top-4 bg-slate-900 border border-slate-800 rounded-xl flex flex-col h-[560px] lg:h-[calc(100vh-9rem)]"
                data-testid="image-ad-chat"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 flex-shrink-0">
                  <h2 className="text-lg font-semibold">Image Chat</h2>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0 bg-slate-950/60">
                  {chatMessages.length === 0 && (
                    <div className="text-sm text-slate-500 italic">
                      Ask about the angle, request research, or talk through the concept.
                    </div>
                  )}
                  {chatMessages.map((m, i) => (
                    <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                      <div className={`inline-block max-w-[85%] rounded px-3 py-2 text-sm ${
                        m.role === "user" ? "bg-indigo-600/30 text-indigo-100" : "bg-slate-800 text-slate-100"
                      }`}>
                        {m.content}
                      </div>
                      {m.sources && m.sources.length > 0 && (
                        <div className="mt-1 text-left text-xs text-slate-400 space-y-0.5">
                          {m.sources.map((s, j) => (
                            <div key={j}>
                              <a href={s.url} target="_blank" rel="noreferrer"
                                 className="text-indigo-300 hover:text-indigo-200 underline">
                                {s.title || s.url}
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-3 border-t border-slate-800 flex-shrink-0 flex gap-2">
                  <input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !chatSending) sendChatMessage(); }}
                    placeholder="Ask Image Chat…"
                    className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                    data-testid="image-ad-chat-input"
                  />
                  <button onClick={sendChatMessage} disabled={chatSending || !chatInput.trim()}
                          className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 text-sm disabled:opacity-50"
                          data-testid="image-ad-chat-send-btn">
                    {chatSending ? "…" : "Send"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// The exact, truthful delivery state for one creative — derived from the
// backend `delivery` summary when present (never claims "delivered" unless the
// file is really in Drive), with a safe fallback to the raw status.
function DeliveryBadge({ c }) {
  const d = c.delivery || {};
  const state = d.delivery_state || c.status;
  const map = {
    delivered: ["Delivered", "text-emerald-400"],
    generated_not_yet_delivered: ["Generated — delivery pending", "text-amber-400"],
    generation_failed: ["Generation failed", "text-rose-400"],
    rejected_qa: ["Rejected (QA)", "text-rose-400"],
    approved: ["Approved", "text-emerald-400"],
    ready: ["Ready", "text-emerald-400"],
  };
  const [label, cls] = map[state] || [state || "unknown", "text-slate-400"];
  const err = d.delivery_error || c.error;
  return (
    <span className={cls} data-testid="image-ad-delivery-state">
      {label}{err ? ` (${err})` : ""}
    </span>
  );
}

// Per-asset actions: Open in Drive, Download, Copy Link. Download hits the
// in-app asset endpoint with ?download=1 (works for pending-delivery bytes too,
// before a Drive link exists); Open/Copy use the real Drive link when present.
function AssetActions({ c }) {
  const [copied, setCopied] = useState(false);
  const driveLink = (c.delivery && c.delivery.drive_web_link) || c.drive_web_link;
  const downloadUrl = c.creative_id
    ? `${API}/creative/image/creatives/${c.creative_id}/asset?download=1`
    : null;
  const btn = "px-2 py-1 rounded bg-slate-700/70 hover:bg-slate-600 text-[11px] font-medium disabled:opacity-40";
  return (
    <div className="flex items-center gap-2" data-testid="image-ad-asset-actions">
      <a
        href={driveLink || undefined}
        target="_blank" rel="noreferrer"
        aria-disabled={!driveLink}
        onClick={(e) => { if (!driveLink) e.preventDefault(); }}
        className={btn + (driveLink ? "" : " pointer-events-none opacity-40")}
        data-testid="image-ad-open-in-drive"
      >
        Open in Drive
      </a>
      <a
        href={downloadUrl || undefined}
        onClick={(e) => { if (!downloadUrl) e.preventDefault(); }}
        className={btn + (downloadUrl ? "" : " pointer-events-none opacity-40")}
        data-testid="image-ad-download"
      >
        Download
      </a>
      <button
        type="button"
        disabled={!driveLink}
        onClick={() => {
          navigator.clipboard?.writeText(driveLink || "");
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className={btn}
        data-testid="image-ad-copy-link"
      >
        {copied ? "Copied" : "Copy Link"}
      </button>
    </div>
  );
}

// Full-size image expansion (issue #145). Centered over a dark backdrop.
// Dismisses on backdrop click, the × button, and Escape. Focuses the close
// button on open and restores focus to the trigger on close. No entrance
// animation, so it is reduced-motion friendly by construction.
export function ImageLightbox({ src, alt, onClose }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeBtnRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Expanded image"}
      data-testid="image-ad-lightbox"
    >
      <button
        ref={closeBtnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white text-2xl leading-none flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
        data-testid="image-ad-lightbox-close"
      >
        ×
      </button>
      <img
        src={src}
        alt={alt || "Expanded image"}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain rounded shadow-2xl"
        data-testid="image-ad-lightbox-image"
      />
    </div>
  );
}

function CreativeReviewRow({ c, selected, onToggle, onRetry, retrying }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const canSelect = c.status === "ready";
  const assetUrl = c.creative_id
    ? `${API}/creative/image/creatives/${c.creative_id}/asset`
    : null;
  const location = (c.delivery && c.delivery.drive_folder_path) || c.drive_folder_path;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded p-3 text-sm space-y-2"
         data-testid="image-ad-creative-row">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {canSelect && (
            <input type="checkbox" checked={selected} onChange={onToggle}
                   data-testid="image-ad-creative-select-checkbox" />
          )}
          <div className="font-mono text-xs text-slate-300">
            {c.creative_name || "(pending name)"}
          </div>
        </div>
        <div className="text-[11px] text-slate-500">
          {c.format} · <DeliveryBadge c={c} />
        </div>
      </div>

      {/* In-app render of the actual generated image (not only in Drive).
          Click to expand it full-size in a lightbox (issue #145). */}
      <div className="flex gap-3">
        {assetUrl && !imgFailed ? (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="w-40 shrink-0 rounded overflow-hidden bg-slate-900 border border-slate-700 flex items-center justify-center cursor-zoom-in hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            style={{ minHeight: "6rem" }}
            aria-label={`Expand ${c.creative_name || "generated image"}`}
            data-testid="image-ad-expand-image"
          >
            <img
              src={assetUrl}
              alt={c.creative_name || "generated image"}
              className="w-full h-auto block"
              loading="lazy"
              onError={() => setImgFailed(true)}
              data-testid="image-ad-inline-image"
            />
          </button>
        ) : (
          <div className="w-40 shrink-0 rounded overflow-hidden bg-slate-900 border border-slate-700 flex items-center justify-center"
               style={{ minHeight: "6rem" }}>
            <span className="text-[11px] text-slate-500 p-2 text-center"
                  data-testid="image-ad-inline-image-fallback">
              Preview unavailable{" "}
              {(c.drive_web_link || (c.delivery && c.delivery.drive_web_link))
                ? "— open in Drive" : ""}
            </span>
          </div>
        )}
        {lightboxOpen && assetUrl && !imgFailed && (
          <ImageLightbox
            src={assetUrl}
            alt={c.creative_name || "generated image"}
            onClose={() => setLightboxOpen(false)}
          />
        )}
        <div className="flex-1 space-y-2 min-w-0">
          <AssetActions c={c} />
          {location && (
            <div className="text-[11px] text-slate-500 truncate" data-testid="image-ad-drive-location"
                 title={location}>
              Location: <span className="text-slate-400">{location}</span>
            </div>
          )}
          {c.ad_copy && (
            <div className="bg-slate-900/60 rounded p-2 space-y-1">
              <CopyRow label="Ad Headline" value={c.ad_copy.platform_headline} />
              <CopyRow label="Primary Text" value={c.ad_copy.platform_primary_text} />
              <CopyRow label="CTA button" value={c.ad_copy.platform_cta_button} />
            </div>
          )}
          {c.status === "generated_pending_delivery" && (
            <button onClick={onRetry} disabled={retrying}
                    className="px-3 py-1.5 rounded bg-amber-600/90 hover:bg-amber-500 text-xs font-medium disabled:opacity-50"
                    data-testid="image-ad-retry-delivery-btn">
              {retrying ? "Retrying…" : "Retry delivery"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs text-slate-400 shrink-0">{label}:</div>
      <div className="text-xs text-slate-200 flex-1 truncate">{value || "—"}</div>
      <button
        onClick={() => {
          navigator.clipboard?.writeText(value || "");
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="text-[10px] text-indigo-300 hover:text-indigo-200 shrink-0"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

const FORMAT_ASPECT = {
  META_SQUARE: 1, META_LANDSCAPE: 1200 / 628, META_STORY: 1080 / 1920, TIKTOK_STORY: 1080 / 1920,
};

function placementStyle(position) {
  const [vert, horiz] = (position || "top-center").split("-");
  const style = { position: "absolute", maxWidth: "80%" };
  if (vert === "top") style.top = "8%"; else if (vert === "bottom") style.bottom = "8%";
  else style.top = "50%";
  if (horiz === "left") style.left = "5%";
  else if (horiz === "right") { style.right = "5%"; }
  else { style.left = "50%"; style.transform = (style.transform || "") + " translateX(-50%)"; }
  if (vert === "center") style.transform = (style.transform || "") + " translateY(-50%)";
  return style;
}

function LayoutPreview({ brief, format }) {
  const aspect = FORMAT_ASPECT[format] || 1;
  return (
    <div
      className="relative bg-slate-800 border border-slate-700 rounded mx-auto overflow-hidden"
      style={{ width: "260px", height: `${260 / aspect}px` }}
      data-testid="image-ad-layout-preview"
    >
      <div className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-600">
        (generated image goes here)
      </div>
      {brief.primary_headline && (
        <div style={placementStyle(brief.text_overlay_placement?.headline_position)}
             className="bg-black/70 text-white text-[11px] px-2 py-1 rounded text-center">
          {brief.primary_headline}
        </div>
      )}
      {brief.cta_text && (
        <div style={placementStyle(brief.text_overlay_placement?.cta_position)}
             className="bg-emerald-600/90 text-white text-[10px] px-2 py-1 rounded text-center">
          {brief.cta_text}
        </div>
      )}
    </div>
  );
}

const MAX_PALETTE_COLOURS = 3;
const DEFAULT_SWATCH_COLOUR = "#888888";

export function ColourPaletteEditor({ value, onChange }) {
  const swatches = value.length ? value : [];

  const updateAt = (idx, hex) => {
    const next = [...swatches];
    next[idx] = hex;
    onChange(next);
  };

  const addSwatch = () => {
    if (swatches.length >= MAX_PALETTE_COLOURS) return;
    onChange([...swatches, DEFAULT_SWATCH_COLOUR]);
  };

  const removeAt = (idx) => {
    onChange(swatches.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="image-ad-brief-colour_palette">
      {swatches.map((hex, idx) => (
        <div key={idx} className="flex flex-col items-center gap-1">
          <div className="relative">
            <label
              className="block w-9 h-9 rounded border border-slate-700 cursor-pointer overflow-hidden"
              style={{ backgroundColor: hex || DEFAULT_SWATCH_COLOUR }}
              title={hex}
            >
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(hex) ? hex : DEFAULT_SWATCH_COLOUR}
                onChange={(e) => updateAt(idx, e.target.value)}
                className="opacity-0 w-full h-full cursor-pointer"
              />
            </label>
            <button
              type="button"
              onClick={() => removeAt(idx)}
              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-700 hover:bg-rose-600 text-white text-[9px] leading-4 text-center"
              title="Remove colour"
            >
              ×
            </button>
          </div>
          <span className="text-[10px] font-mono text-slate-400">{hex}</span>
        </div>
      ))}
      {swatches.length < MAX_PALETTE_COLOURS && (
        <button
          type="button"
          onClick={addSwatch}
          className="w-9 h-9 rounded border border-dashed border-slate-600 text-slate-400 hover:border-slate-400 hover:text-slate-200 text-lg leading-none flex items-center justify-center"
          title="Add colour"
        >
          +
        </button>
      )}
    </div>
  );
}
